import Paginate from "@/components/Paginate";
import ShoppingListContainer from "@/components/ShoppingList/ShoppingListContainer";
import SplitViewLayout from "@/components/SplitViewLayout";
import { useHeaderContext } from "@/contexts/HeaderContext";
import craft from "@/lib/craft";
import getCommonStaticProps from "@/lib/getCommonStaticProps";
import QueryRecipes from "@/queries/QueryRecipes.graphql";
import QueryRecipiesIndex from "@/queries/QueryRecipiesIndex.graphql";
import updateSeomaticWithPageNumber from "@/src/lib/updateSeomaticWithPageNumber";
import { INDEX_LIMIT } from "@/src/recipes/lib/constants";
import clsx from "clsx";
import EntryGrid from "components/EntryGrid";
import Head from "next/head";
import { useEffect } from "react";
import { Seomatic } from "react-seomatic";

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export async function getStaticProps({ params }) {
  // Fetch common static properties
  const commonStaticProps = await getCommonStaticProps();

  const pageNumber = parseInt(params.pageNumber);

  const response = await craft([
    {
      document: QueryRecipiesIndex,
    },
    {
      document: QueryRecipes,
      variables: { limit: INDEX_LIMIT, offset: (pageNumber - 1) * INDEX_LIMIT },
    },
  ]);

  const { pageEntry } = response[0];
  const { recipes, recipeCount } = response[1];

  const pageCount = Math.floor(recipeCount / INDEX_LIMIT);

  // Update seomatic with page number
  if (pageEntry.seomatic && pageNumber > 1) {
    try {
      pageEntry.seomatic = updateSeomaticWithPageNumber(
        pageEntry.seomatic,
        pageNumber
      );
    } catch (error) {
      console.error("Error updating SEO metadata:", error);
    }
  }

  return {
    props: {
      ...commonStaticProps,
      pageEntry,
      seomatic: pageEntry.seomatic,
      recipeCount,
      recipes,
      pageNumber,
      pageCount,
    },
    revalidate: process.env.VERCEL_ENV !== "production" ? 1 : 3600,
  };
}

const RecipesIndex = ({ pageEntry, recipes, pageCount, pageNumber }) => {
  // Show the header border for this page
  const { setShowBorder } = useHeaderContext();
  useEffect(() => {
    setShowBorder(true);
    return () => setShowBorder(false);
  }, []);

  // Use the last but one's recipe image as the hero if one isn't specified by the pageEntry (the index)
  const images =
    !!pageEntry.image && pageEntry.image.length > 0
      ? pageEntry.image
      : recipes[recipes?.length - 5]?.image;

  return (
    <>
      <Seomatic Head={Head} {...pageEntry?.seomatic} />
      <ShoppingListContainer hideOnScroll />
      <div className={clsx("RecipeIndex__header", "border-b")}>
        <SplitViewLayout
          heading={pageEntry?.title || "Recipes"}
          introduction={pageEntry?.summary || "No recipes index summary."}
          images={images}
        />
      </div>

      <div className={clsx("RecipeIndex__grid", "px-6 py-6 lg:px-12 lg:py-12")}>
        <EntryGrid entries={recipes} />
      </div>

      {pageCount > 1 && (
        <div className={clsx("RecipeIndex__paginate", "border-t")}>
          <Paginate
            pageCount={pageCount}
            activePage={pageNumber}
            baseUri="/recipes"
          />
        </div>
      )}
    </>
  );
};

export default RecipesIndex;
